"use client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { IResource } from "@gettactic/api";
import { api } from "@/lib/api/api";
import { Modal } from "@/ui/index";
import { Button } from "@cmp-src/primitives/button";

export interface DeleteResourceProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  onClose: Function;
  resources: IResource[];
  setSideDrawerOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DeleteResource = ({
  onClose,
  resources,
  setSideDrawerOpen
}: DeleteResourceProps) => {
  const queryClient = useQueryClient();
  const { register, handleSubmit } = useForm();
  const [loading, setLoading] = useState<boolean>(false);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      setLoading(true);
      const promises = resources.map(async (res) =>
        api.client.organizations.resourceDelete(res.id)
      );
      return { res: await Promise.all(promises) };
    },

    onSuccess: async () => {
      if (setSideDrawerOpen) {
        setSideDrawerOpen(false);
      }
      setLoading(false);
      await queryClient.invalidateQueries({
        queryKey: ["organizations.resourcesAll"]
      });
      await queryClient.invalidateQueries({
        queryKey: ["organizations.resources"]
      });
      onClose();
      toast.success("Deleted successfully");
    },

    onSettled: () => {
      setLoading(false);
    }
  });

  const onDeleteSubmit = async () => {
    await deleteMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit(onDeleteSubmit)}>
      <div>
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <svg
              className="h-6 w-6 text-red-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <div className="mt-2">
              <p className="text-base text-neutral-800">
                Are you sure you want to delete{" "}
                {resources.length > 1
                  ? ` ${resources.length} resources`
                  : ` this resource`}
                ?
              </p>
            </div>
          </div>
        </div>
      </div>
      <Modal.Footer>
        <Button
          className="w-full sm:w-auto"
          variant="outline"
          onClick={() => onClose()}
        >
          Cancel
        </Button>
        <Button
          className="w-full sm:ml-3 sm:w-auto"
          variant="destructive"
          type="submit"
          disabled={loading}
          isLoading={loading}
        >
          Delete
        </Button>
      </Modal.Footer>
    </form>
  );
};
